import { Button } from '@mui/material';
import { Stack } from '@mui/system';
import { taskActions } from '@common-reducers/TasksReducer';
import { handleUpload } from '@common-services/upload';
import moment from 'moment';
import React, { useCallback, useRef, useState } from 'react'
import { CirclePicker } from 'react-color';
import { BiImageAdd } from 'react-icons/bi';
import { groupcolors } from '@common/theme';
import classes from "@common-components/tasks-panel/form/bt-color-picker.module.scss";
import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { namespace } from '@common/config/constants';
import { useTranslation } from 'react-i18next';


type Props = {
    handleColorChange: (color: { hex: string }) => void;
    handlePhotoChange: (url: string) => void;
}

type FileVersion = { url: string; type: string; filename: string; updatedAt: string; version: number };
type FileDetails = FileVersion & { versions: FileVersion[] };

export default function PhotoOrColorSelect({
    handlePhotoChange,
    handleColorChange,
}: Props) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch()
    const fileRef = useRef<HTMLInputElement | null>(null);
    const [loading, showLoader] = useState(false);

    function addFiles(e: React.MouseEvent) {
        e.stopPropagation();
        if (!fileRef.current) return;
        fileRef.current.value = "";
        fileRef.current.click();
    }

    const uploadFiles = useCallback(async (files: FileList | null): Promise<FileDetails[] | undefined> => {
        if (files) {
            const filesArray = [...files];
            const [file] = filesArray;
            if (file.size >= 4000000) {
                dispatch(taskActions.setTaskSnackbarMessage(t('photoOrColorSelect.cannotUploadBigFiles')))
            }
            const fileType = file.type
            if (!fileType.includes('image')) {
                dispatch(taskActions.setTaskSnackbarMessage(t('photoOrColorSelect.onlyImages')))
            }
            else {
                const promises = filesArray.map(async (file): Promise<FileDetails> => {
                    return new Promise<FileDetails>((resolve, reject) => {
                        const reader = new FileReader();
                        const mimeType: string = file.type;
                        const filename: string = file.name;
                        reader.addEventListener("load", async () => {
                            const base64file = reader.result as string;
                            const urlLink = await uploadFile(base64file, mimeType);
                            const updatedAt = moment().format('DD/MM/YYYY HH:mm:ss');
                            const fileDetails: FileDetails = {
                                url: urlLink ?? "",
                                type: mimeType,
                                filename,
                                updatedAt,
                                version: 1,
                                versions: [{ url: urlLink ?? "", type: mimeType, filename, updatedAt, version: 1 }],
                            };
                            resolve(fileDetails);
                        });
                        reader.onerror = reject;
                        reader.readAsDataURL(file);
                    });
                });
                const currentFilesUploaded = await Promise.all(promises);
                return currentFilesUploaded;
            }
        }
    }, [dispatch, t])

    const onNewFilesAdded = useCallback(async (newFiles: FileList | null) => {
        showLoader(true);
        const result = await uploadFiles(newFiles);
        if (!result || result.length === 0) {
            showLoader(false);
            return;
        }
        const [fileListUploaded] = result;
        const uploadedFileUrl = JSON.stringify(fileListUploaded.url).split(`"`)[1]
        if (fileListUploaded) {
            handlePhotoChange(uploadedFileUrl)
        }
        showLoader(false);
    }, [handlePhotoChange, uploadFiles])

    const uploadFile = async (base64file: string, mimeType: string): Promise<string | undefined> => {
        try {
            const response = await handleUpload(base64file, mimeType) as { uploadResponse?: { url?: string }, image_url?: string };
            if (response?.uploadResponse?.url) {
                return response.image_url;
            }
        } catch (err) {
            console.log(err);
        }
    };



    return (
        <Stack direction='column' sx={{
            p: 3,
        }}
        >
            <div style={{
                marginBottom: '14px',
                paddingBottom: '10px',
                borderBottom: '1px solid var(--tasks-cell-border)'
            }}>
                <Button
                    onClick={(e) => {
                        addFiles(e);
                    }}
                    startIcon={<BiImageAdd />}
                    style={{
                        cursor: 'pointer',
                        textTransform: 'none',
                        border: '1px dashed #e5e5e5',
                        borderRadius: 8,
                        height: 56,
                        color: 'var(--tasks-text-secondary)',
                        paddingInline: '20px',
                        width: 200,
                        maxWidth: 200,
                        fontFamily: 'inherit',
                        fontWeight: 600,
                    }}
                    value="upload image">
                    {t('photoOrColorSelect.uploadImage')}
                </Button>


                <p style={{
                    marginTop: 6,
                    textAlign: 'center',
                    fontSize: 11,
                    color: 'var(--tasks-text-secondary)',
                }}>
                </p>
                <input
                    onChange={(e) => {
                        onNewFilesAdded(e.target.files);
                    }}
                    ref={fileRef}
                    type="file"
                    multiple={false}
                    hidden
                />
            </div>
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}>
                <CirclePicker
                    onChange={handleColorChange}
                    width="180px"
                    circleSize={20}
                    circleSpacing={10}
                    colors={groupcolors}
                />
            </div>
        </Stack>
    )
}